// prebuiltDomainSetsCalculator.js
// Transforms API response cumulativeData into PrebuiltSets component format

import { relevanceFromDistance } from "../utils";

/**
 * Transform API response into prebuilt domain sets format
 * @param {Object} apiResponse - The full API response
 * @returns {Object} Formatted prebuilt domain sets
 */
export const calculatePrebuiltDomainSets = (apiResponse) => {
	try {
		const body = apiResponse?.body || apiResponse?.data?.body || apiResponse;

		if (!body) {
			console.warn("No body found in API response");
			return {};
		}

		const { domain: domains = [], cumulativeData = [], keywords = [] } = body;

		if (!domains.length || !cumulativeData.length) {
			console.warn("No domains or cumulative data found");
			return {};
		}

		// Create domain lookup map
		const domainLookup = createDomainLookup(domains);

		// Transform each segment into a prebuilt set
		const prebuiltSets = {};

		cumulativeData.forEach((segment, index) => {
			const setKey = generateSetKey(segment.segment);
			const segmentDomains = getDomainsForSegment(segment, domainLookup);

			if (segmentDomains.length === 0) {
				return;
			}

			// Calculate aggregated metrics
			const metrics = calculateAggregatedMetrics(segmentDomains);
			const categories = extractUniqueCategories(segmentDomains);
			const relevanceScore = calculateAverageRelevanceScore(segmentDomains);

			prebuiltSets[setKey] = {
				name: formatSegmentName(segment.segment),
				description: generateDescription(
					segment.segment,
					segmentDomains.length,
				),
				relevanceScore: relevanceScore,
				domains: segmentDomains.map((domain) => formatDomainForSet(domain)),
				marketRequests: calculateTotalMarketRequests(segmentDomains),
				totalReach: calculateTotalReach(segmentDomains),
				metrics: {
					ctrVtr: `${metrics.avgCtr}/${metrics.avgVtr}`,
					ctr: {
						value: metrics.avgCtr,
						trend: "up",
					},
					viewability: {
						value: metrics.avgViewability,
					},
					cpm: {
						value: metrics.avgCpm,
					},
					vtr: {
						value: metrics.avgVtr,
					},
					completionRate: {
						value: metrics.avgCompletionRate,
						trend: "up",
					},
					avgReachPerDay: formatLargeNumber(segment.reach),
				},
				categories: categories.slice(0, 3),
				segmentData: segment,
				rawDomains: segmentDomains,
				cumulativeData: segment,
			};
		});

		return prebuiltSets;
	} catch (error) {
		console.error("Error calculating prebuilt domain sets:", error);
		return {};
	}
};

/**
 * Generate a unique key for the set
 */
const generateSetKey = (segmentName) => {
	return segmentName
		.toLowerCase()
		.replace(/\s+/g, "_")
		.replace(/[^a-z0-9_]/g, "");
};

/**
 * Get domains for a specific segment
 */
const getDomainsForSegment = (segment, domainLookup) => {
	if (!segment.domainIds) {
		return [];
	}

	// Handle both string and array formats for domainIds
	let domainIds = [];

	if (Array.isArray(segment.domainIds)) {
		// New format: domainIds is already an array of strings
		domainIds = segment.domainIds.map((id) => {
			const numericId = Number(id);
			return isNaN(numericId) ? id : numericId;
		});
	} else if (typeof segment.domainIds === "string") {
		// Old format: domainIds is a string that looks like an array
		const cleanedDomainIds = segment.domainIds
			.replace(/^\[/, "") // Remove opening bracket
			.replace(/\]$/, ""); // Remove closing bracket

		domainIds = cleanedDomainIds
			.split(",")
			.map((id) => id.trim())
			.map((id) => {
				const numericId = Number(id);
				return isNaN(numericId) ? id : numericId;
			});
	} else if (Array.isArray(segment.domainIds)) {
		domainIds = segment.domainIds;
	}

	const foundDomains = [];

	domainIds.forEach((id) => {
		// Try multiple lookup approaches
		const domain =
			domainLookup[id] || domainLookup[String(id)] || domainLookup[Number(id)];
		if (domain) {
			foundDomains.push(domain);
		}
	});

	return foundDomains;
};

/**
 * Create domain lookup map
 */
const createDomainLookup = (domains) => {
	return domains.reduce((acc, domain) => {
		if (domain.id) {
			acc[domain.id] = domain;
			acc[String(domain.id)] = domain;
		}
		return acc;
	}, {});
};

/**
 * Calculate average reach per day with proper formatting
 */
const calculateAvgReachPerDay = (segment, domains) => {
	// Use the reach directly from the cumulative data
	const segmentReach = segment.reach || 0;
	return segmentReach;
};

/**
 * Calculate aggregated metrics for a set of domains using correct formulas
 */
const calculateAggregatedMetrics = (domains) => {
	if (!domains.length) return getDefaultMetrics();

	// CTR calculation
	const totalClicks = domains.reduce(
		(sum, domain) => sum + (domain.totalCprClicks || 0),
		0,
	);
	const totalImpressionsWithClickMacro = domains.reduce(
		(sum, domain) => sum + (domain.totalImpressionsWithClickMacro || 0),
		0,
	);

	const aggregatedCTR =
		totalImpressionsWithClickMacro > 0
			? totalClicks / totalImpressionsWithClickMacro
			: 0;

	// Completion rate calculation
	const totalCompleteFirst = domains.reduce(
		(sum, domain) => sum + (domain.totalCompleteFirstOccurrence || 0),
		0,
	);
	const totalStartFirst = domains.reduce(
		(sum, domain) => sum + (domain.totalStartFirstOccurrence || 0),
		0,
	);

	const aggregatedCompletionRate =
		totalStartFirst > 0 ? (totalCompleteFirst * 100.0) / totalStartFirst : 0;

	// VTR calculation using correct aggregated formula
	const aggregatedVTR =
		totalStartFirst > 0 ? (totalCompleteFirst / totalStartFirst) * 100 : 0;

	// Viewability calculation using correct aggregated formula
	const totalViewables = domains.reduce(
		(sum, domain) => sum + (domain.totalViewables || 0),
		0,
	);
	const totalViewabilityMeasures = domains.reduce(
		(sum, domain) => sum + (domain.totalViewabilityMeasures || 0),
		0,
	);
	const aggregatedViewability =
		totalViewabilityMeasures > 0
			? (totalViewables / totalViewabilityMeasures) * 100
			: 0;

	// Bid guidance calculation using real formula: SUM(totalBidSpendInUsd) / SUM(totalImpressions)
	const totalBidSpendInUsd = domains.reduce(
		(sum, domain) => sum + (domain.totalBidSpendInUsd || 0),
		0,
	);
	const totalImpressions = domains.reduce(
		(sum, domain) => sum + (domain.totalImpressions || 0),
		0,
	);
	const bidGuidance =
		totalImpressions > 0 ? totalBidSpendInUsd / totalImpressions / 1000 : 0;

	return {
		avgCtr: `${aggregatedCTR.toFixed(3)}%`,
		avgViewability: `${aggregatedViewability.toFixed(1)}%`,
		avgCpm: `$${(bidGuidance).toFixed(2)}`,
		avgVtr: `${aggregatedVTR.toFixed(1)}%`,
		avgCompletionRate: `${aggregatedCompletionRate.toFixed(1)}%`,
	};
};

/**
 * Get default metrics when no data is available
 */
const getDefaultMetrics = () => ({
	avgCtr: "0.000%",
	avgViewability: "0.0%",
	avgCpm: "$0.00",
	avgVtr: "0.0%",
	avgCompletionRate: "0.0%",
});

/**
 * Calculate average relevance score
 */
const calculateAverageRelevanceScore = (domains) => {
	// Calculate individual relevance score for each domain using the Domains sigmoid
	const individualRelevanceScores = [];

	domains.forEach((domain) => {
		const domainScore = domain?.score;
		if (
			domainScore !== undefined &&
			domainScore !== null &&
			!isNaN(parseFloat(domainScore))
		) {
			// Domains sigmoid relevance curve (see constants.js)
			const score = parseFloat(domainScore);
			individualRelevanceScores.push(relevanceFromDistance(score, "DOMAINS"));
		}
	});

	// Calculate the average of all individual relevance scores
	if (individualRelevanceScores.length === 0) {
		return 85;
	}

	const sumOfScores = individualRelevanceScores.reduce(
		(total, score) => total + score,
		0,
	);
	const averageRelevanceScore = sumOfScores / individualRelevanceScores.length;
	return Math.round(averageRelevanceScore);
};

/**
 * Extract unique categories from domains
 */
const extractUniqueCategories = (domains) => {
	const categoryCount = {};

	domains.forEach((domain) => {
		if (domain.categories && Array.isArray(domain.categories)) {
			domain.categories.forEach((category) => {
				if (category && category.trim()) {
					categoryCount[category] = (categoryCount[category] || 0) + 1;
				}
			});
		}
	});

	// Sort by frequency and return top categories
	return Object.entries(categoryCount)
		.sort(([, a], [, b]) => b - a)
		.map(([category]) => category);
};

/**
 * Calculate total reach across domains
 */
const calculateTotalReach = (domains) => {
	if (!domains.length) return "0";

	const totalReach = domains.reduce((sum, domain) => {
		return sum + (domain.domainReach || 0);
	}, 0);

	return formatLargeNumber(totalReach);
};

/**
 * Format domain for set display using correct formulas
 */
const formatDomainForSet = (domain) => {
	// CTR calculation
	const clicks = domain.totalCprClicks || 0;
	const impressionsWithClickMacro = domain.totalImpressionsWithClickMacro || 0;
	const ctrValue =
		impressionsWithClickMacro > 0
			? ((clicks / impressionsWithClickMacro) * 100).toFixed(3)
			: "0.000";

	// VTR calculation using correct formula
	const completeFirst = domain.totalCompleteFirstOccurrence || 0;
	const startFirst = domain.totalStartFirstOccurrence || 0;
	const vtrValue =
		startFirst > 0 ? ((completeFirst / startFirst) * 100).toFixed(1) : "0.0";

	// Completion rate calculation (same as VTR in this case)
	const completionRate =
		startFirst > 0 ? ((completeFirst / startFirst) * 100).toFixed(1) : "0.0";

	// Viewability calculation using correct formula
	const viewables = domain.totalViewables || 0;
	const viewabilityMeasures = domain.totalViewabilityMeasures || 0;
	const viewabilityValue =
		viewabilityMeasures > 0
			? ((viewables / viewabilityMeasures) * 100).toFixed(1)
			: "0.0";

	let publisherArray = [];
	if (domain.publisheraccountid && Array.isArray(domain.publisheraccountid)) {
		publisherArray = domain.publisheraccountid;
	} else if (
		domain.publisheraccountid &&
		typeof domain.publisheraccountid === "string"
	) {
		// If it's a string, split every 9 digits
		publisherArray = domain.publisheraccountid.match(/.{1,9}/g) || [
			domain.publisheraccountid,
		];
	} else if (domain.publisher) {
		// Handle other publisher field formats
		if (Array.isArray(domain.publisher)) {
			publisherArray = domain.publisher;
		} else if (typeof domain.publisher === "string") {
			publisherArray = domain.publisher.match(/.{1,9}/g) || [domain.publisher];
		}
	}
	return {
		name: domain.domainName || "Unknown Domain",
		domain: domain.domainName,
		score: domain.score,
		totalCprMarketRequests: domain.totalCprMarketRequests || 0,
		avgBidCpm: domain.avgBidCpm || 0,
		metrics: {
			ctrVtr: `${ctrValue}%/${vtrValue}%`,
			viewability: `${viewabilityValue}%`,
			avgBidCpm: domain.avgBidCpm || 0,
			cpm: domain.calculated_bid_guidance
				? `$${(domain.calculated_bid_guidance / 100).toFixed(2)}`
				: "N/A",
			categories: domain.categories || [],
			publisher: publisherArray.join(", "),
			reach: formatLargeNumber(domain.domainReach || 0),
			relevance: domain.score ? Math.round(domain.score * 100) : 85,
			vtr: `${vtrValue}%`,
			completionRate: `${completionRate}%`,
			clicks: domain.totalCprClicks || 0,
			impressions: domain.totalImpressions || 0,
			impressionsWithClickMacro: domain.totalImpressionsWithClickMacro || 0,
			rawCtr: domain.calculated_ctr,
			rawVtr: parseFloat(vtrValue),
			rawCompletionRate: parseFloat(completionRate),
		},
	};
};

/**
 * Format segment name for display
 */
const formatSegmentName = (segmentName) => {
	return segmentName || "Unknown Segment";
};

/**
 * Generate description based on segment
 */
const generateDescription = (segmentName, domainCount) => {
	const descriptions = {
		"Direct Publishers": "Publishers available through direct deals only",
		"Performance Optimized": "Domains optimized for high performance metrics",
		"Relevance Focused": "Most relevant domains for your search criteria",
	};

	return (
		descriptions[segmentName] ||
		`Curated set of ${domainCount} domains for targeted advertising`
	);
};

/**
 * Calculate total market requests across domains in a segment
 */
const calculateTotalMarketRequests = (domains) => {
	if (!domains.length) return "0";

	const totalMarketRequests = domains.reduce((sum, domain) => {
		return sum + (domain.totalCprMarketRequests || 0);
	}, 0);

	return formatLargeNumber(totalMarketRequests);
};

/**
 * Format large numbers with appropriate suffixes
 */
const formatLargeNumber = (num) => {
	if (!num || num === 0) return "0";

	if (num >= 1000000) {
		return `${(num / 1000000).toFixed(1)}M`;
	} else if (num >= 1000) {
		return `${(num / 1000).toFixed(1)}K`;
	} else if (num >= 1) {
		return num.toFixed(1);
	}
	return num.toString();
};

// Export additional utility functions if needed
export const utils = {
	formatLargeNumber,
	calculateAggregatedMetrics,
	formatSegmentName,
	generateDescription,
};

// Export individual calculation functions for testing
export const calculations = {
	/**
	 * Calculate CTR using the correct formula
	 * @param {Array} domains - Array of domain objects
	 * @returns {number} CTR percentage
	 */
	calculateCTR: (domains) => {
		const totalClicks = domains.reduce(
			(sum, domain) => sum + (domain.totalCprClicks || 0),
			0,
		);
		const totalImpressionsWithClickMacro = domains.reduce(
			(sum, domain) => sum + (domain.totalImpressionsWithClickMacro || 0),
			0,
		);

		return totalImpressionsWithClickMacro > 0
			? (totalClicks / totalImpressionsWithClickMacro) * 100
			: 0;
	},

	/**
	 * Calculate Completion Rate using the correct formula
	 * @param {Array} domains - Array of domain objects
	 * @returns {number} Completion rate percentage
	 */
	calculateCompletionRate: (domains) => {
		const totalCompleteFirst = domains.reduce(
			(sum, domain) => sum + (domain.totalCompleteFirstOccurrence || 0),
			0,
		);
		const totalStartFirst = domains.reduce(
			(sum, domain) => sum + (domain.totalStartFirstOccurrence || 0),
			0,
		);

		return totalStartFirst > 0
			? (totalCompleteFirst * 100.0) / totalStartFirst
			: 0;
	},

	/**
	 * Calculate VTR using the correct formula
	 * @param {Array} domains - Array of domain objects
	 * @returns {number} VTR percentage
	 */
	calculateVTR: (domains) => {
		const totalCompleteFirst = domains.reduce(
			(sum, domain) => sum + (domain.totalCompleteFirstOccurrence || 0),
			0,
		);
		const totalStartFirst = domains.reduce(
			(sum, domain) => sum + (domain.totalStartFirstOccurrence || 0),
			0,
		);

		return totalStartFirst > 0
			? (totalCompleteFirst / totalStartFirst) * 100
			: 0;
	},

	/**
	 * Calculate Viewability using the correct formula
	 * @param {Array} domains - Array of domain objects
	 * @returns {number} Viewability percentage
	 */
	calculateViewability: (domains) => {
		const totalViewables = domains.reduce(
			(sum, domain) => sum + (domain.totalViewables || 0),
			0,
		);
		const totalViewabilityMeasures = domains.reduce(
			(sum, domain) => sum + (domain.totalViewabilityMeasures || 0),
			0,
		);

		return totalViewabilityMeasures > 0
			? (totalViewables / totalViewabilityMeasures) * 100
			: 0;
	},
};
